import React from 'react';
import { ChevronDown, FolderPlus, Circle, CircleDot, Loader2, Plus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useWorkbookQuery } from '@/hooks/content-generator/use-workbook-query';
import { useNavigate } from 'react-router-dom';
import { URL_PARAMS } from '@/config/url-parameters';

interface ProjectSelectionDropdownProps {
  selectedProjectId: string | null;
  onProjectChange: (projectId: string | null) => void;
}

const ProjectSelectionDropdown: React.FC<ProjectSelectionDropdownProps> = ({
  selectedProjectId,
  onProjectChange,
}) => {
  const navigate = useNavigate();
  
  const { data: workbookData, isLoading: isLoadingProjects } = useWorkbookQuery({ 
    filterByStatus: 'all' // Get all non-archived projects
  });
  
  const projects = workbookData?.projects || [];
  
  // Find selected project info for display
  const selectedProject = selectedProjectId 
    ? projects.find(project => project.id === selectedProjectId)
    : null;
  
  // Display text for trigger
  const triggerText = selectedProject ? selectedProject.title : 'Select a Project';
  
  // Handle create project navigation
  const handleCreateProject = () => {
    navigate(`/project-manager?${URL_PARAMS.MODALS.CREATE_PROJECT}=true`);
  };
  
  return (
    <div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={cn(
              "w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium",
              "bg-white/30 dark:bg-white/10 backdrop-blur-sm",
              "border-2 border-purple-200/40 dark:border-purple-700/40 focus:border-purple-300/70 dark:focus:border-purple-600/50",
              "text-gray-700 dark:text-gray-200",
              "shadow-sm hover:shadow-md transition-all duration-300",
              "gap-3"
            )}
            disabled={isLoadingProjects}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <FolderPlus 
                size={16} 
                className={cn(
                  selectedProject 
                    ? 'text-purple-600 dark:text-purple-400'
                    : 'text-gray-500 dark:text-gray-400'
                )} 
              />
              <span className="truncate">{triggerText}</span>
              {isLoadingProjects && (
                <Loader2 className="h-4 w-4 animate-spin text-purple-500 ml-auto" />
              )}
            </div>
            <ChevronDown size={16} className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
          </motion.button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent 
          align="start" 
          className="w-[var(--radix-dropdown-menu-trigger-width)] bg-white/90 dark:bg-black/90 backdrop-blur-md border border-purple-200/40 dark:border-purple-700/40"
          sideOffset={4}
        >
          <DropdownMenuLabel>Select Project</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {isLoadingProjects ? (
            <div className="flex items-center justify-center p-3">
              <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
              <span className="ml-2 text-sm text-gray-500">Loading projects...</span>
            </div>
          ) : (
            <>
              {/* Private option */}
              <DropdownMenuItem
                onClick={() => onProjectChange(null)}
                className="flex items-center gap-3 p-3"
              >
                <div className="flex items-center justify-center w-4 h-4">
                  {selectedProjectId === null ? (
                    <CircleDot className="h-4 w-4 text-blue-600" />
                  ) : (
                    <Circle className="h-4 w-4 text-gray-400" />
                  )}
                </div>
                <span className="flex-1 text-sm">
                  No Project
                </span>
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              
              {/* Project options */}
              {projects.map((project) => {
                const isSelected = selectedProjectId === project.id;
                
                return (
                  <DropdownMenuItem
                    key={project.id}
                    onClick={() => onProjectChange(project.id)}
                    className="flex items-center gap-3 p-3"
                  >
                    <div className="flex items-center justify-center w-4 h-4">
                      {isSelected ? (
                        <CircleDot className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Circle className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                    <span className="flex-1 truncate text-sm">
                      {project.title}
                    </span>
                  </DropdownMenuItem>
                );
              })}
              
              {projects.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No projects available
                </div>
              )}
              
              <DropdownMenuSeparator />
              
              {/* Create Project option */}
              <DropdownMenuItem
                onClick={handleCreateProject}
                className="flex items-center gap-3 p-3 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
              >
                <div className="flex items-center justify-center w-4 h-4">
                  <Plus className="h-4 w-4" />
                </div>
                <span className="flex-1 text-sm font-medium">
                  Create Project
                </span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ProjectSelectionDropdown;